// Switch (toggle pill) — custom implementation sem Radix.
//
// Histórico:
//  - Radix-switch tinha bugs de data-state no Preact compat.
//  - V1 custom usava `translate-x-4` Tailwind, mas dependia do JIT
//    compilar a classe E do Tailwind aplicar — em algum build/cache o
//    thumb ficava parado em translate-x-0 mesmo com value=true.
//  - Esta V2 usa `style={{ transform: ... }}` inline pra eliminar
//    qualquer dependência do tailwind compilar utilities específicas.
//    Cores do track também usam style inline (hsl vars) ao invés de
//    classes Tailwind, garantindo que track ON e thumb à direita
//    sempre acompanhem o mesmo `value`.
//
// API compatível com shadcn/Radix:
//   <Switch checked={x} onCheckedChange={(v) => setX(v)} disabled={...} />

import * as React from "react"

import { cn } from "@app/ui/lib/utils"

export interface SwitchProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "onChange"> {
  checked?: boolean;
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
}

// Dimensões fixas em px — track 36 (9*4), thumb 16 (4*4), padding 2 cada lado.
// Travel do thumb = 36 - 2*2 - 16 = 16px. Mesmos números do Radix base.
const TRACK_HEIGHT = 20;
const TRACK_WIDTH = 36;
const THUMB_SIZE = 16;
const TRAVEL = 16;

const Switch = React.forwardRef<HTMLButtonElement, SwitchProps>(
  (
    {
      className,
      checked,
      defaultChecked,
      onCheckedChange,
      disabled,
      onClick,
      style,
      ...props
    },
    ref
  ) => {
    // Controlado se `checked` foi passado; senão usa estado interno.
    const [internal, setInternal] = React.useState<boolean>(!!defaultChecked);
    const isControlled = checked !== undefined;
    const value = isControlled ? !!checked : internal;

    function handleClick(ev: React.MouseEvent<HTMLButtonElement>) {
      if (onClick) onClick(ev);
      if (ev.defaultPrevented || disabled) return;
      const next = !value;
      if (!isControlled) setInternal(next);
      onCheckedChange?.(next);
    }

    // Track: dimensões + cor (inline pra não depender de utility class JIT).
    // Cores via CSS vars (--primary / cinza) — respeita tema/dark mode.
    const trackStyle: React.CSSProperties = {
      width: `${TRACK_WIDTH}px`,
      height: `${TRACK_HEIGHT}px`,
      backgroundColor: value
        ? "hsl(var(--primary))"
        : "hsl(220 9% 76%)",
      ...((typeof style === "object" && style !== null ? style : {}) as React.CSSProperties)
    };
    // Thumb: posição via translateX(0|16px). Centralizado vertical via top.
    const thumbStyle: React.CSSProperties = {
      width: `${THUMB_SIZE}px`,
      height: `${THUMB_SIZE}px`,
      transform: `translateX(${value ? TRAVEL : 0}px)`
    };

    // Branches duplicados pra satisfazer linter (aria-checked precisa ser
    // literal estática "true"/"false" no JSX, não uma expressão).
    if (value) {
      return (
        <button
          ref={ref}
          type="button"
          role="switch"
          aria-checked="true"
          data-state="checked"
          disabled={disabled}
          onClick={handleClick}
          style={trackStyle}
          className={cn(
            "relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
            className
          )}
          {...props}
        >
          <span
            aria-hidden="true"
            style={thumbStyle}
            className="pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform"
          />
        </button>
      );
    }
    return (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked="false"
        data-state="unchecked"
        disabled={disabled}
        onClick={handleClick}
        style={trackStyle}
        className={cn(
          "relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50",
          className
        )}
        {...props}
      >
        <span
          aria-hidden="true"
          style={thumbStyle}
          className="pointer-events-none block rounded-full bg-white shadow-lg ring-0 transition-transform"
        />
      </button>
    );
  }
);
Switch.displayName = "Switch";

export { Switch }
